// src/taskpane/modules/i18n_legal.ts

export const LEGAL_DICT = {
    "en": {
        "link-privacy": "Privacy Policy",
        "link-support": "Customer Support",
        "pp-browser-title": "Privacy Policy - DeepBid",
        "pp-title": "DeepBid Privacy Policy",
        "pp-date-label": "Release Date: ",
        "pp-date": "December 20, 2025",
        "pp-publisher-label": "Publisher: ",
        "pp-publisher-name": "Hansen Lee",
        "pp-intro": "As an AI-assisted tool, this privacy policy is intended to inform you how this add-in handles, collects, and protects your information.",
        "pp-s1-title": "1. Principles of Information Processing",
        "pp-s1-content": "DeepBid adopts a 'local-first' and 'stateless forwarding' architecture. Our core principle is: we do not persistently store any of the user's business-sensitive data on any of our own servers.",
        "pp-s2-title": "2. Types of Information Collected and Processed",
        "pp-s2-l1-t": "Document Content",
        "pp-s2-l1-c": "When you call AI functions (e.g., Generate, Polish), the add-in will obtain the selected text or heading context. This data is only used to construct AI prompts and is released immediately after the request is completed.",
        "pp-s2-l2-t": "API Keys",
        "pp-s2-l2-c": "The API keys of the AI service providers you configure are only stored encrypted in your local device's localStorage.",
        "pp-s2-l3-t": "Project Context",
        "pp-s2-l3-c": "Project background information (RedBook) and constraints (Rules) are stored in the current Word document's local metadata and your browser cache.",
        "pp-s3-title": "3. Data Transmission and Third-party Services",
        "pp-s3-l1-t": "Transmission Security",
        "pp-s3-l1-c": "All data is transmitted via the encrypted HTTPS protocol.",
        "pp-s3-l2-t": "Gateway Forwarding",
        "pp-s3-l2-c": "Your requests are processed through our backend deployed on Cloudflare for logic assembly and forwarding. Our backend service acts only as a real-time gateway and does not log your prompts or document content.",
        "pp-s3-l3-t": "Third-party AI Providers",
        "pp-s3-l3-c": "Your text will be sent to the AI provider you have configured (e.g., DeepSeek). Processing logic follows that provider's privacy policy.",
        "pp-s4-title": "4. User Control Over Data",
        "pp-s4-intro": "You have full control over your data:",
        "pp-s4-l1": "You can clear local API configurations at any time through the 'System Settings'.",
        "pp-s4-l2": "You can use the 'Clean Document' function to delete project info and IDs stored in the Word document's metadata with one click.",
        "pp-s4-l3": "You can uninstall this add-in at any time; uninstallation will automatically remove all local caches.",
        "pp-s5-title": "5. Disclaimer",
        "pp-s5-content": "DeepBid provides AI-assisted writing suggestions. AI-generated content may contain biases or errors. Users should review the final content of the bid document and bear full responsibility.",
        "pp-s6-title": "6. Contact Us",
        "pp-s6-intro": "If you have any questions about this privacy policy, please contact us via:",
        "pp-s6-email-label": "Email: ",
        "pp-footer-rights": "All rights reserved.",
        "sp-browser-title": "Support - DeepBid",
        "sp-title": "DeepBid Support & Help",
        "sp-intro": "Thank you for choosing DeepBid. We are committed to improving your bid document writing efficiency through AI technology. If you encounter any problems during use, please refer to the following guide or contact us directly.",
        "sp-contact-title": "Contact Support",
        "sp-contact-msg": "If you need technical support, bug reports, or feature suggestions, please email us at:",
        "sp-s1-title": "1. Quick Start Guide",
        "sp-s1-l1-title": "Configure Model",
        "sp-s1-l1-desc": ": Click the settings icon at the top of the add-in to enter 'Model Configuration'. You can add your own DeepSeek or compatible OpenAI API service.",
        "sp-s1-l2-title": "Smart Selection",
        "sp-s1-l2-desc": ": DeepBid has smart selection awareness. If you have not selected any text, the add-in will automatically capture the paragraph where the current cursor is located for analysis.",
        "sp-s1-l3-title": "Clean Document",
        "sp-s1-l3-desc": ": Before submitting a bid, please click the 'Clean' icon at the top. This will remove all AI-assisted metadata stored in the document with one click to ensure business security.",
        "sp-s2-title": "2. Frequently Asked Questions (FAQ)",
        "sp-q1": "1. Why is there no response when clicking 'Generate' or 'Polish'?",
        "sp-a1": "Please check your network connection and whether the API Key in 'Model Configuration' is correct. You can click 'Test & Save' to verify interface connectivity.",
        "sp-q2": "2. What languages does the add-in support?",
        "sp-a2": "DeepBid perfectly supports a bilingual interface in Chinese and English. The add-in will automatically detect your Office environment language and complete the automatic adaptation.",
        "sp-a3-prefix": "DeepBid does not persistently store any of your document content on the server. All data processing follows our ",
        "sp-a3-link": "Privacy Policy",
        "sp-reviewer-title": "Note for Microsoft Reviewers:",
        "sp-reviewer-msg": "DeepBid provides a Reviewer Mode. If you do not have an available API Key, the system will automatically inject a demo configuration (DeepBid Demo). In this mode, requests will be sent to a controlled demo backend, and you can test all core AI functions without configuration.",
        "sp-footer-link": "Privacy Policy",
        "sp-footer-rights": "All rights reserved."
    },
    "zh": {
        "link-privacy": "隐私协议",
        "link-support": "客户支持",
        "pp-browser-title": "隐私政策 - DeepBid",
        "pp-title": "DeepBid 隐私政策",
        "pp-date-label": "发布日期：",
        "pp-date": "2025年12月20日",
        "pp-publisher-label": "发布者：",
        "pp-publisher-name": "Hansen Lee",
        "pp-intro": "作为一个AI辅助工具，本隐私政策旨在告知您本插件如何处理、收集和保护您的信息。",
        "pp-s1-title": "一、 信息处理原则",
        "pp-s1-content": "DeepBid 采用“本地优先”与“无状态转发”的设计架构。核心原则是：不在任何自有服务器上持久化存储用户的商业敏感数据。",
        "pp-s2-title": "二、 收集与处理的信息类型",
        "pp-s2-l1-t": "文档内容",
        "pp-s2-l1-c": "当您调用 AI 功能（如生成、润色）时，插件会获取您选中的文本或标题上下文。这些数据仅用于构建 AI 指令，并在请求完成后立即释放。",
        "pp-s2-l2-t": "配置凭据 (API Key)",
        "pp-s2-l2-c": "您配置的 AI 服务商 API 密钥仅加密存储在您本地设备的 localStorage 中。",
        "pp-s2-l3-t": "项目上下文 (RedBook/Rules)",
        "pp-s2-l3-c": "项目背景信息和约束条件存储在当前 Word 文档的本地元数据及您的浏览器缓存中。",
        "pp-s3-title": "三、 数据传输与第三方服务",
        "pp-s3-l1-t": "传输安全",
        "pp-s3-l1-c": "所有数据均通过加密的 HTTPS 协议传输。",
        "pp-s3-l2-t": "中转转发",
        "pp-s3-l2-c": "您的请求通过部署在 Cloudflare 的后台进行逻辑组装与转发。我们的后端服务仅作为实时网关，不记录您的指令或文档内容。",
        "pp-s3-l3-t": "第三方 AI 服务商",
        "pp-s3-l3-c": "您的文本将发送至您自行配置的 AI 提供商（如 DeepSeek）进行处理。处理逻辑遵循该服务商的隐私协议。",
        "pp-s4-title": "四、 用户对数据的控制权",
        "pp-s4-intro": "您拥有对数据的完全控制权：",
        "pp-s4-l1": "您可以通过插件的“系统设置”随时清除本地 API 配置。",
        "pp-s4-l2": "您可以使用“净化文档”功能一键删除存储在 Word 文档元数据中的项目背景和 ID。",
        "pp-s4-l3": "您可以随时通过 Word 设置卸载本插件，卸载将自动移除所有本地缓存。",
        "pp-s5-title": "五、 免责声明",
        "pp-s5-content": "DeepBid 基于大语言模型提供辅助撰写建议。AI 生成的内容可能存在偏差，用户应当对最终提交的投标文件内容进行审核并承担全部责任。",
        "pp-s6-title": "六、 联系我们",
        "pp-s6-intro": "如果您对本隐私政策有任何疑问，请通过以下方式联系：",
        "pp-s6-email-label": "邮箱：",
        "pp-footer-rights": "保留所有权利。",
        "sp-browser-title": "客户支持 - DeepBid",
        "sp-title": "DeepBid 支持与帮助",
        "sp-intro": "感谢您选择 DeepBid。我们致力于通过 AI 技术提升您的投标文件撰写效率。如果您在使用过程中遇到任何问题，请查阅以下指南或直接联系我们。",
        "sp-contact-title": "联系支持",
        "sp-contact-msg": "如果您需要技术支持、报告漏洞或提出功能建议，请发送邮件至：",
        "sp-s1-title": "一、 快速入门指南",
        "sp-s1-l1-title": "配置模型",
        "sp-s1-l1-desc": "：点击插件顶部的设置图标，进入“模型配置”。您可以添加自己的 DeepSeek 或兼容 OpenAI 接口的服务。",
        "sp-s1-l2-title": "智能选区",
        "sp-s1-l2-desc": "：DeepBid 具备智能选区感知功能。如果您未选中任何文本，插件会自动抓取当前光标所在的段落进行分析。",
        "sp-s1-l3-title": "净化文档",
        "sp-s1-l3-desc": "：在提交标书前，请点击顶部的“净化”图标。这将一键移除文档中存储的所有 AI 辅助元数据，确保商业安全。",
        "sp-s2-title": "二、 常见问题 (FAQ)",
        "sp-q1": "1. 为什么点击“生成”或“润色”没有反应？",
        "sp-a1": "请检查您的网络连接以及“模型配置”中的 API Key 是否正确。您可以点击“测试并保存”来验证接口连通性。",
        "sp-q2": "2. 插件支持哪些语言？",
        "sp-a2": "DeepBid 完美支持中英文双语界面。插件会自动检测您的 Office 环境语言并完成自动适配。",
        "sp-a3-prefix": "DeepBid 不会在服务器上持久化存储您的任何文档内容。所有数据处理均遵循我们的 ",
        "sp-a3-link": "隐私政策",
        "sp-reviewer-title": "提示（针对 Microsoft 审核员 / Note for Reviewers）:",
        "sp-reviewer-msg": "DeepBid 提供了演示模式（Reviewer Mode）。如果您没有可用的 API Key，系统会自动注入一个演示配置（DeepBid Demo）。该模式下请求将发送至受控的演示后端，您可以免配置测试所有 AI 核心功能。",
        "sp-footer-link": "隐私政策",
        "sp-footer-rights": "保留所有权利。"
    }
};
